import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Zap, Layers, Shield, Smartphone, Bell, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  CURRENT_VERSION,
  LS_SEEN_KEY,
  WHATS_NEW_ENTRIES,
} from "@/config/whatsNew.config";

const ICONS = {
  zap: Zap,
  layers: Layers,
  shield: Shield,
  smartphone: Smartphone,
  bell: Bell,
  sparkles: Sparkles,
};

/**
 * WhatsNewModal — one-shot "what changed since your last visit" surface.
 *
 * Triggers on Dashboard mount if the user hasn't seen this iteration's
 * highlights yet. Compares `CURRENT_VERSION` against
 * `localStorage[LS_SEEN_KEY]`; on dismiss, writes CURRENT_VERSION back
 * so the modal only appears again after a real release.
 *
 * Skipped for brand-new accounts (users younger than 24h) so the tour
 * doesn't collide with the onboarding flow — those users already got a
 * fresh product experience and don't need a "here's what changed" note.
 */
export default function WhatsNewModal() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Suppress for accounts less than a day old — they got the full
    // onboarding tour and don't need a "what's new" too.
    try {
      const created = user.created_at ? new Date(user.created_at).getTime() : 0;
      const ageMs = Date.now() - created;
      if (created && ageMs < 24 * 60 * 60 * 1000) return;
    } catch { /* ignore */ }
    // Suppress if the onboarding tour is still open (LS flag missing
    // means it hasn't been completed/skipped yet). Two modals stacked
    // on Dashboard mount is jarring; defer this one until next visit.
    try {
      if (localStorage.getItem("pfo_tour_completed") !== "1") return;
      const seen = localStorage.getItem(LS_SEEN_KEY);
      if (seen === CURRENT_VERSION) return;
    } catch { /* private mode */ }
    // Small delay lets the dashboard render + settle first so the
    // modal doesn't compete with the initial paint.
    const timer = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(timer);
  }, [user]);

  const handleClose = () => {
    try { localStorage.setItem(LS_SEEN_KEY, CURRENT_VERSION); }
    catch { /* private mode */ }
    setOpen(false);
  };

  if (!open) return null;

  const entry = WHATS_NEW_ENTRIES[0]; // newest — matches CURRENT_VERSION

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeup"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={handleClose}
      data-testid="whats-new-modal"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden"
        style={{
          background: "var(--bg-primary)",
          borderColor: "var(--border-default)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="whats-new-close"
          aria-label={t("whats_new.close", { defaultValue: "Dismiss" })}
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-accent)] transition-colors"
        >
          <X size={16} />
        </button>

        <div
          className="px-6 pt-6 pb-4 border-b"
          style={{
            background: "linear-gradient(135deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.10) 100%)",
            borderColor: "var(--border-default)",
          }}
        >
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-1"
               style={{ color: "var(--accent-primary)" }}>
            {t("whats_new.eyebrow", { defaultValue: "What's new" })} · {entry.date}
          </div>
          <div className="text-lg font-semibold tracking-tight leading-tight"
               style={{ fontFamily: "var(--font-heading)", color: "var(--text-primary)" }}
               data-testid="whats-new-headline">
            {t(`whats_new.${entry.version}.headline`, { defaultValue: entry.headline })}
          </div>
        </div>

        <ul className="px-6 py-5 space-y-3">
          {entry.bullets.map((b, i) => {
            const Icon = ICONS[b.icon] || Sparkles;
            return (
              <li key={`${entry.version}-${b.icon}-${i}`} className="flex items-start gap-3">
                <div
                  className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: "rgba(45,90,64,0.10)" }}
                >
                  <Icon size={14} style={{ color: "var(--accent-primary)" }} />
                </div>
                <div className="text-[13px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  {t(`whats_new.${entry.version}.bullets.${i}`, { defaultValue: b.text })}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="px-6 pb-6 pt-1 flex justify-end">
          <button
            onClick={handleClose}
            data-testid="whats-new-cta"
            className="btn-primary text-[13px] px-4 py-2"
          >
            {t("whats_new.cta", { defaultValue: "Got it" })}
          </button>
        </div>
      </div>
    </div>
  );
}
